import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Filter, Search, X } from "lucide-react";

import ProductCard from "../components/ProductCard";
import useSEO from "../lib/useSEO";
import { fetchCategories, fetchProducts } from "../lib/api";

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get("category") || "all";
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(initialCat);
  const [query, setQuery] = useState("");
  const [mobileFilter, setMobileFilter] = useState(false);

  useSEO({
    title: "Shop — Custom Apparel Sublimasi | Ace Sublimasi Nusantara",
    description:
      "Katalog jersey futsal custom, jaket anime, haori Jepang, racing apparel, crop top, dan streetwear sublimasi. Bebas desain, tanpa minimum order.",
    keywords:
      "shop apparel custom, katalog jersey futsal, jaket anime, haori jepang, racing apparel",
    canonical: window.location.origin + "/shop",
  });

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchProducts({
      category: activeCat !== "all" ? activeCat : undefined,
      q: query || undefined,
    })
      .then(setProducts)
      .finally(() => setLoading(false));

    if (activeCat === "all") searchParams.delete("category");
    else searchParams.set("category", activeCat);
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCat, query]);

  const Filters = () => (
    <div className="space-y-1" data-testid="shop-filters">
      <button
        data-testid="filter-all"
        onClick={() => setActiveCat("all")}
        className={`w-full text-left px-4 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${
          activeCat === "all"
            ? "bg-[#CCFF00] text-black"
            : "text-white hover:bg-[#0e0e0e]"
        }`}
      >
        Semua Produk
      </button>
      {categories.map((c) => (
        <button
          key={c.slug}
          data-testid={`filter-${c.slug}`}
          onClick={() => setActiveCat(c.slug)}
          className={`w-full text-left px-4 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${
            activeCat === c.slug
              ? "bg-[#CCFF00] text-black"
              : "text-white hover:bg-[#0e0e0e]"
          }`}
        >
          {c.name}
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="shop-page" className="border-b border-[#1f1f1f]">
      {/* Hero */}
      <section className="border-b border-[#1f1f1f] py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-3">
            / Katalog
          </p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase leading-none text-white">
            Shop the <span className="text-[#CCFF00]">Drop.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base text-[#A3A3A3]">
            Semua kategori bisa di-custom 100%. Harga di sini adalah harga starting (mulai dari).
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="hidden md:block md:col-span-3 lg:col-span-3">
          <div className="sticky top-24">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#CCFF00] mb-4">
              Filter Kategori
            </p>
            <div className="border border-[#262626] divide-y divide-[#262626]">
              <Filters />
            </div>
            <div className="mt-6">
              <label className="text-xs font-bold tracking-[0.25em] uppercase text-[#CCFF00]">
                Cari Produk
              </label>
              <div className="mt-2 flex border border-[#262626] focus-within:border-[#CCFF00] transition-colors">
                <span className="px-3 flex items-center text-[#A3A3A3]">
                  <Search className="w-4 h-4" />
                </span>
                <input
                  data-testid="shop-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Jersey, anime, racing..."
                  className="flex-1 bg-transparent text-sm text-white py-3 outline-none"
                />
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile filter button */}
        <div className="md:hidden col-span-full flex items-center gap-3">
          <button
            data-testid="mobile-filter-toggle"
            onClick={() => setMobileFilter(true)}
            className="btn-ghost flex-1 !py-3"
          >
            <Filter className="w-4 h-4" /> Filter
          </button>
          <div className="flex-1 flex border border-[#262626]">
            <span className="px-3 flex items-center text-[#A3A3A3]">
              <Search className="w-4 h-4" />
            </span>
            <input
              data-testid="mobile-shop-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari..."
              className="flex-1 bg-transparent text-sm text-white py-3 outline-none"
            />
          </div>
        </div>

        {mobileFilter && (
          <div className="fixed inset-0 z-50 bg-[#050505]/95 backdrop-blur-md md:hidden p-6">
            <div className="flex items-center justify-between mb-6">
              <p className="font-display text-2xl uppercase text-white">Filter</p>
              <button
                data-testid="mobile-filter-close"
                onClick={() => setMobileFilter(false)}
                className="p-2 text-white"
              >
                <X />
              </button>
            </div>
            <div className="border border-[#262626] divide-y divide-[#262626]">
              <Filters />
            </div>
            <button
              onClick={() => setMobileFilter(false)}
              className="btn-primary w-full mt-6"
            >
              Terapkan
            </button>
          </div>
        )}

        {/* Products */}
        <div className="md:col-span-9 lg:col-span-9" data-testid="shop-grid">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-[#A3A3A3]">
              {loading ? "Memuat..." : `${products.length} produk`}
            </p>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="border border-[#262626] aspect-[4/5] bg-[#0a0a0a] animate-pulse"
                />
              ))}
            </div>
          ) : products.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          ) : (
            <div
              data-testid="shop-empty"
              className="border border-[#262626] p-10 text-center"
            >
              <p className="font-display text-2xl uppercase text-white">
                Belum ada produk di kategori ini
              </p>
              <p className="mt-3 text-sm text-[#A3A3A3]">
                Tetap bisa custom! Chat admin untuk request kategori spesifik.
              </p>
              <Link
                to="/custom"
                className="btn-primary mt-6 inline-flex"
                data-testid="shop-empty-cta"
              >
                Custom Sekarang
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
